import './BookingModal.css';
import { useEffect, useMemo, useState } from 'react';

import useUser from '../../hooks/useUser';
import useInputString from '../../hooks/useInputString';

import MyModal from '../UI/MyModal/MyModal';
import ClassicButton from '../UI/ClassicButton/ClassicButton';
import PaymentWidget from '../PaymentWidget/PaymentWidget';

import { IEventPageResponseModel } from '../../interfaces/responses/IEventPageResponseModel';
import { DashCircleFill, PlusCircleFill, XLg } from 'react-bootstrap-icons';
import { IBookingRequestModel } from '../../interfaces/requests/IBookingRequestModel';
import AlertModal from '../UI/MyAlert/MyAlert';
import useScreenWidth from '../../hooks/useScreenWidth';
import { useNavigate } from 'react-router-dom';

interface BookingModalProps {
    event: IEventPageResponseModel;
    мodalVisible: boolean;
    setModalVisible: (visible: boolean) => void;
    onCloseModal: () => void;
    onPaymentSuccess: () => void; 
}

const BookingModal: React.FC<BookingModalProps> = ({ event, мodalVisible, setModalVisible, onCloseModal }) => {
    const user = useUser();
    const email = useInputString('');
    const [bookings, setBookings] = useState<IBookingRequestModel[]>([]);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [agreement, setAgreement] = useState<boolean>(false);
    const [ticketCounts, setTicketCounts] = useState<{ [key: string]: number }>(
        event.ticketTypes.reduce((acc, ticket) => {
            acc[ticket.type] = 0;
            return acc;
        }, {} as { [key: string]: number })
    );
    const [alertVisible, setAlertVisible] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const screenWidth = useScreenWidth();
    const navigate = useNavigate();
    
    const resetModalState = () => {
        setCurrentStep(1);
        setAgreement(false);
        setTicketCounts(
            event.ticketTypes.reduce((acc, ticket) => {
                acc[ticket.type] = 0;
                return acc;
            }, {} as { [key: string]: number })
        );
        setBookings([]);
        email.setValue(user.user?.email ?? '');
    };
    
    useEffect(() => {
        if (мodalVisible) {
            resetModalState();
            email.setValue(user.user?.email ?? '');
        }
    }, [мodalVisible]);
    
    const handlePaymentSuccess = () => {
        setAlertMessage('Оплата прошла успешно! Проверьте электронную почту.'); 
        setAlertVisible(true); 
        onCloseModal(); 
        setBookings([]);
    };
    
    const handlePlus = (type: string) => {
        setTicketCounts(prevCounts => {
            const newCount = prevCounts[type] + 1;
            updateBookings(type, newCount);
            return {
                ...prevCounts,
                [type]: newCount,
            };
        });
    };
    
    const handleMinus = (type: string) => {
        setTicketCounts(prevCounts => {
            const newCount = Math.max(prevCounts[type] - 1, 0);
            updateBookings(type, newCount);
            return {
                ...prevCounts,
                [type]: newCount,
            };
        });
    };
    
    const totalAmount = useMemo(() => {
        return event.ticketTypes.reduce((total, ticket) => {
            return total + (ticketCounts[ticket.type] * ticket.price);
        }, 0);
    }, [ticketCounts, event.ticketTypes]);
    
    const nextStep = () => {
        const hasSelectedTicket = Object.values(ticketCounts).some(count => count > 0);
        
        if (!hasSelectedTicket) {
            setAlertMessage('Вы не выбрали билет!');
            setAlertVisible(true);
            return;
        }
        
        setCurrentStep(currentStep + 1);
    };
    
    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };
    
    const updateBookings = (type: string, count: number) => {
        setBookings(prevBookings => {
            const updatedBookings = [...prevBookings];
            const ticket = event.ticketTypes.find(t => t.type === type);
            if (ticket) {
                const existingBookingIndex = updatedBookings.findIndex(b => b.ticket_type_id === ticket.id);
                
                if (existingBookingIndex > -1) {
                    if (count === 0) {
                        updatedBookings.splice(existingBookingIndex, 1);
                    } else {
                        updatedBookings[existingBookingIndex].quantity = count;
                    }
                } else if (count > 0) {
                    updatedBookings.push({
                        user_id: user.user?.userId,
                        ticket_type_id: ticket.id,
                        quantity: count,
                    });
                }
            }
            return updatedBookings;
        });
    };
    
    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className='bookingModal_tab1'>
                        <h3>{event.title}</h3>
                        
                        <div className='bookingModal_tickets'>
                            {event.ticketTypes.map((t) => (
                                <div className='bookingModal_ticket' key={t.type}>
                                    <h3>{t.type}</h3>

                                    <div className='bookingModal_ticket_price'>{t.price} ₽</div>

                                    <div className='bookingModal_ticket_counter'>
                                        <DashCircleFill
                                            className='bookingModal_ticket_counterIcon'
                                            size={screenWidth > 450 ? 23 : 20}
                                            onClick={() => handleMinus(t.type)}
                                        />
                                        <span>{ticketCounts[t.type]}</span>
                                        <PlusCircleFill
                                            className='bookingModal_ticket_counterIcon'
                                            size={screenWidth > 450 ? 23 : 20}
                                            onClick={() => handlePlus(t.type)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <div className='bookingModal_footer'>
                            <p><b>Сумма: {totalAmount} ₽</b></p>
                            <ClassicButton
                                fontSize='15px'
                                padding='5px 10px'
                                borderRadius='20px'
                                onClick={nextStep}
                            >
                                Далее
                            </ClassicButton>
                        </div>
                    </div>
                );
            
            case 2:
                return (
                    <div className='bookingModal_tab2'>
                        <h3>Проверьте детали заказа</h3>
                        
                        <div className='bookingModal_tab2_order'>
                            <ul>
                                {event.ticketTypes.map((t) => (
                                    ticketCounts[t.type] > 0 && (
                                        <li key={t.type}>
                                            {t.type}: {ticketCounts[t.type]} шт. — {ticketCounts[t.type] * t.price} ₽
                                        </li>
                                    )
                                ))}
                            </ul>
                            <p><b>Сумма: {totalAmount} ₽</b></p>
                        </div>
                        
                        <div className='bookingModal_tab2_formContainer'>
                            <form className="form">
                                <div className='form_inputContainer'>
                                    <p>Почта для получения билета</p>
                                    <input
                                        className='email'
                                        type="email"
                                        value={email.value}
                                        onChange={email.onChange}
                                        autoComplete="email"
                                    />
                                </div>
                                <div className='form_inputContainer_agreement'>
                                    <input type="checkbox" defaultChecked={agreement} onChange={(e) => setAgreement(e.target.checked)} />
                                    <p>Я принимаю условия <span className='agreementLink' onClick={() => {
                                        window.open('/privacy-policy', '_blank');
                                    }}>Предоставления услуг</span></p>
                                </div>
                            </form>
                        </div>
                        
                        <div className='bookingModal_footer'>
                            <ClassicButton
                                fontSize='15px'
                                padding='5px 10px'
                                borderRadius='20px'
                                onClick={prevStep}
                            >
                                Назад
                            </ClassicButton>
                            <PaymentWidget
                                amount={totalAmount}
                                description={`Покупка билетов на ${event.title}`}
                                bookings={bookings}
                                email={email.value}
                                agreement={agreement}
                                onPaymentSuccess={handlePaymentSuccess}
                                userEmail={email.value}
                                eventTitle={event.title}
                                eventDate={event.eventDate}
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };
    
    return (
        <>
            <MyModal visible={мodalVisible} setVisible={setModalVisible}>
                <div className='bookingModal'>
                    <div className='bookingModal_header'>
                        <p>
                            {new Date(event.eventDate).toLocaleDateString('ru-RU', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                            })}
                        </p>
                        
                        <p>
                            {new Date(event.eventDate).toLocaleTimeString('ru-RU', {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </p>
                        
                        <XLg className='bookingModal_closeModalIcon' size={20} onClick={() => setModalVisible(false)} />
                    </div>
                    
                    {renderStepContent()}
                    
                </div>
            </MyModal>
            
            <AlertModal visible={alertVisible} setVisible={setAlertVisible} autoCloseTime={2500}>
                {alertMessage}
            </AlertModal>
        </>
    );
}

export default BookingModal;